import React from 'react';
import { Alert, Button, Box, Typography } from '@mui/material';
import { Autorenew as AutorenewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAdminAccess } from '../../hooks/useAdminAccess';

const RENEW_WINDOW_DAYS = 5;

/**
 * One-time (prepaid) license renewal reminder.
 *
 * Shown to org admins within the final RENEW_WINDOW_DAYS of a one-time license,
 * since — unlike a recurring subscription — it does not auto-renew and will lapse
 * to the paywall when it expires.
 */
export const RenewalBanner: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useTenant();
  const { isCognitoAdmin } = useAdminAccess();

  const billing = config.billing;
  const days = billing?.daysLeft;
  const show =
    billing?.payModel === 'license' &&
    days != null &&
    days <= RENEW_WINDOW_DAYS &&
    isCognitoAdmin();
  if (!show) return null;

  const timeLeft =
    days! <= 0
      ? 'Your license ends today.'
      : `Your license ends in ${days} day${days === 1 ? '' : 's'}.`;

  return (
    <Alert
      severity="warning"
      icon={<AutorenewIcon />}
      sx={{
        borderRadius: 0,
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        '& .MuiAlert-message': { width: '100%' },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<AutorenewIcon />}
          onClick={() => navigate('/admin/billing')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Renew now
        </Button>
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body2">
          <strong>{timeLeft}</strong> One-time licenses don’t auto-renew — renew to keep your
          community active.
        </Typography>
      </Box>
    </Alert>
  );
};

export default RenewalBanner;
